import i18next from 'i18next';

import {ads_config} from './config';
import {displayError, makeDraftSticky, makeSticky} from './dom-manipulation';
import {withButtonPending} from '../../common/js/button-pending';
import urls, {localizeAppPageUrl} from '../../common/js/urls';
import {
    createGeneratedPlan,
    deleteDrafts,
    fetchDrafts,
    fetchPlanItemAttempts,
    fetchPlanResults,
    importDraftCsv,
    previewGeneratedPlan,
    schedulePlan,
} from './workspace-api';
import {
    renderPlacementPlan,
} from './placement-plan';
import {openDraftPublicationPlacement} from './draft-publication';
import {isPlacementOperationStorageError} from './placement-operation-storage';
import {
    bindSegmentedTabsAutoSync,
    syncSegmentedTabs,
} from './segmented-tabs';
import {downloadDraftBackupZip} from './backup-export';
import {emptyStateHtml, loadingStateHtml} from '../../common/js/empty-state';
import {showToast} from '../../common/js/feature-toast';
import {collectErrorMessages} from '../../common/js/error-messages';
import {
    createPostingOperationAttemptTracker,
    getPostingOperationErrorMessage,
} from './posting-operation-attempt';
import {
    clearDraftImportReport,
    focusDraftImportReport,
    normalizeDraftImportResult,
    showDraftImportReport,
} from './draft-import-feedback';

const WORKSPACE_TABS = ['olx-ads', 'drafts'];
const t = (key, defaultValue, options = {}) => i18next.t(key, {defaultValue, ...options});
const ADS_FILTER_MOBILE_QUERY = '(max-width: 767.98px)';
let pendingDeleteDraftIds = [];
let pendingDeleteDraftTrigger = null;
let draftFilterDrawerSnapshot = null;
let draftFilterDrawerCommitted = false;
let draftPublicationOpenFailureToast = null;
const generatedPlanAttempt = createPostingOperationAttemptTracker({
    storageKey: 'adsPostingOperationAttempt:generated-plan:v1',
});

const createDraftImportIdempotencyKey = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    const values = new Uint8Array(16);
    if (window.crypto?.getRandomValues) {
        window.crypto.getRandomValues(values);
    } else {
        values.forEach((_, index) => {
            values[index] = Math.floor(Math.random() * 256);
        });
    }
    values[6] = (values[6] & 0x0f) | 0x40;
    values[8] = (values[8] & 0x3f) | 0x80;
    const hex = Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

const getDraftImportAttempt = (file) => {
    if (!ads_config.draftImportAttempt || ads_config.draftImportAttempt.file !== file) {
        ads_config.draftImportAttempt = {
            file,
            idempotencyKey: createDraftImportIdempotencyKey(),
        };
    }
    return ads_config.draftImportAttempt;
};

const getDraftImportErrorMessage = (error) => {
    if (error?.error_code === 'draft_import_in_progress') {
        return t('ads:workspace.csvImportInProgress', 'This draft import is already in progress. Try again shortly.');
    }
    if (error?.error_code === 'draft_import_key_conflict') {
        return t('ads:workspace.csvImportKeyConflict', 'This retry key belongs to another file. Select the file again.');
    }
    const messages = collectErrorMessages(error?.errors || error?.error || error);
    return messages.join(', ') || t(
        'ads:workspace.csvImportRequestFailed',
        'Draft import failed. Try again.',
    );
};

const normalizeWorkspaceTab = (tab) => WORKSPACE_TABS.includes(tab) ? tab : 'olx-ads';
const normalizeUrlPath = (url) => {
    try {
        return new URL(url, window.location.href).pathname.replace(/\/?$/, '/');
    } catch (error) {
        return '';
    }
};
const getWorkspacePageBasePath = () => {
    const match = window.location.pathname.match(/^(.*\/)?inventories(?:\/drafts)?\/?$/);
    if (match) {
        return `${match[1] || '/'}inventories/`;
    }
    return new URL(urls.adsPage, window.location.href).pathname.replace(/\/?$/, '/');
};
const getWorkspaceTabUrl = (tab) => (
    tab === 'drafts' ? `${getWorkspacePageBasePath()}drafts/` : getWorkspacePageBasePath()
);
const getWorkspaceTabFromLocation = () => (
    normalizeUrlPath(window.location.href) === normalizeUrlPath(getWorkspaceTabUrl('drafts')) ? 'drafts' : 'olx-ads'
);
const updateWorkspaceTabUrl = (tab) => {
    if (!window.history || typeof window.history.pushState !== 'function') {
        return;
    }
    const targetUrl = getWorkspaceTabUrl(tab);
    if (normalizeUrlPath(window.location.href) === normalizeUrlPath(targetUrl)) {
        return;
    }
    window.history.pushState({adsWorkspaceTab: tab}, '', targetUrl);
};
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (char) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
}[char]));
const safeClassToken = (value) => String(value ?? '').replace(/[^a-zA-Z0-9_-]/g, '');
const extractWorkspaceErrorMessage = (error) => {
    if (error && typeof error === 'object') {
        if (error.message) {
            return error.message;
        }
        if (error.errors) {
            return JSON.stringify(error.errors);
        }
    }
    return String(error || t('ads:workspace.loadError', 'Could not load data.'));
};

const arrayFromSelectValue = (value) => Array.isArray(value)
    ? value.map(String)
    : String(value || '').split(',').map((item) => item.trim()).filter(Boolean);

const setDraftVirtualSelectValue = (selector, value) => {
    const element = document.querySelector(selector);
    if (!element) {
        return;
    }
    if (typeof element.setValue === 'function') {
        element.setValue(value);
        $(element).trigger('change');
        return;
    }
    if (typeof element.setValueMethod === 'function') {
        element.setValueMethod(value);
        $(element).trigger('change');
        return;
    }
    $(selector).val(value).trigger('change');
};

const getCategoryName = (categoryId) => {
    const category = (ads_config.rareData.categories.data || []).find((item) => Number(item.id) === Number(categoryId));
    return category ? category.name : '';
};

const getFormSubmitButtons = (form) => {
    const formId = form && form.id ? form.id : '';
    const formSelector = formId ? `button[type="submit"][form="${formId}"]` : '';
    const internalButtons = form ? $(form).find('button[type="submit"], input[type="submit"]') : $();
    return formSelector ? internalButtons.add($(formSelector)) : internalButtons;
};

const createDraftCategoryTree = (leafCategoryId) => {
    const categories = ads_config.rareData.categories.data || [];
    const ancestors = [];
    let currentId = leafCategoryId;
    const visited = new Set();

    while (currentId && !visited.has(Number(currentId))) {
        visited.add(Number(currentId));
        const category = categories.find((item) => Number(item.id) === Number(currentId));
        if (!category) {
            break;
        }
        ancestors.unshift(category);
        currentId = category.parent_id;
    }

    return ancestors.map((category) => `<li>${escapeHtml(category.name)}</li>`).join('');
};

const getDraftLocationParts = (draft) => {
    const locationData = draft.location_data || {};
    const parts = [
        locationData.city_name,
        locationData.district_name,
        locationData.zone_name,
        locationData.region_name,
    ].filter(Boolean);

    return [...new Set(parts)];
};

const getDraftLocationLabel = (draft) => getDraftLocationParts(draft).join(', ');

const getDraftLocationFilterValues = (draft) => {
    const locationData = draft.location_data || {};
    return [
        locationData.region_id ? `region:${locationData.region_id}` : '',
        locationData.city_id ? `city:${locationData.city_id}` : '',
        locationData.district_id ? `district:${locationData.district_id}` : '',
        locationData.zone_id ? `zone:${locationData.zone_id}` : '',
    ].filter(Boolean);
};

const getPrimaryDraftLocationValue = (draft) => {
    const locationData = draft.location_data || {};
    if (locationData.zone_id) {
        return `zone:${locationData.zone_id}`;
    }
    if (locationData.district_id) {
        return `district:${locationData.district_id}`;
    }
    if (locationData.city_id) {
        return `city:${locationData.city_id}`;
    }
    if (locationData.region_id) {
        return `region:${locationData.region_id}`;
    }
    return '';
};

const getDraftNumericPrice = (draft) => Number(draft.price || 0);
const normalizeDraftId = (draftId) => String(draftId || '');
const isDraftSelected = (draftId) => ads_config.selectedDraftIds.map(String).includes(normalizeDraftId(draftId));
const getSelectedDraftIds = () => ads_config.selectedDraftIds.map(normalizeDraftId).filter(Boolean);

const formatDraftPrice = (draft) => {
    if (draft.price_budget) {
        return t('ads:workspace.priceBudget', 'Free');
    }
    if (draft.price_trade) {
        return t('ads:workspace.priceTrade', 'Exchange');
    }

    const priceValue = getDraftNumericPrice(draft);
    const currency = ads_config.currencies[draft.currency_code] || draft.currency_code || '';
    if (!priceValue) {
        return draft.price_negotiable ? t('ads:workspace.priceNegotiable', 'Negotiable') : '';
    }

    return `${priceValue} ${currency}${draft.price_negotiable ? ` · ${t('ads:workspace.priceNegotiable', 'Negotiable')}` : ''}`;
};

const formatDateTime = (value) => {
    if (!value) {
        return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return String(value);
    }
    return date.toLocaleString(i18next.language, {dateStyle: 'short', timeStyle: 'short'});
};

const draftBadgesHtml = (draft) => {
    const badges = [];
    if (draft.status === 'archived') {
        badges.push(`<span class="badge text-bg-secondary">${t('ads:workspace.statusArchived', 'Archived')}</span>`);
    }
    if (Number(draft.published_count || 0) > 0) {
        badges.push(`<span class="badge text-bg-light">${t('ads:workspace.publishedCountBadge', 'Published: {{count}}', {count: draft.published_count})}</span>`);
    }
    if (Array.isArray(draft.active_plan_refs) && draft.active_plan_refs.length) {
        badges.push(`<span class="badge text-bg-info">${t('ads:workspace.inPlansBadge', 'In plans: {{count}}', {count: draft.active_plan_refs.length})}</span>`);
    }
    return `<div class="workspace-draft-badges">${badges.join('')}</div>`;
};

const draftPublicationMetaHtml = (draft) => {
    const recent = Array.isArray(draft.recent_publications) ? draft.recent_publications : [];
    const activePlans = Array.isArray(draft.active_plan_refs) ? draft.active_plan_refs : [];
    const lastPublishedAt = formatDateTime(draft.last_published_at);

    if (!recent.length && !activePlans.length && !lastPublishedAt) {
        return '';
    }

    const recentHtml = recent.map((item) => {
        const account = item.target_account_name || item.target_account_id || '';
        return `<span class="workspace-draft-meta-pill">OLX #${escapeHtml(item.olx_ad_id)}${account ? ` · ${escapeHtml(account)}` : ''}</span>`;
    }).join('');
    const plansHtml = activePlans.map((item) => (
        `<span class="workspace-draft-meta-pill">#${item.plan_id} · ${escapeHtml(item.plan_name || '')} · ${escapeHtml(item.item_status || '')}</span>`
    )).join('');

    return `
        <div class="workspace-draft-publication-meta">
            ${lastPublishedAt ? `<div>${t('ads:workspace.lastPublishedAt', 'Last published')}: ${escapeHtml(lastPublishedAt)}</div>` : ''}
            ${recentHtml ? `<div>${t('ads:workspace.recentPublications', 'Recent OLX ads')}: ${recentHtml}</div>` : ''}
            ${plansHtml ? `<div>${t('ads:workspace.activePlanRefs', 'Active plans')}: ${plansHtml}</div>` : ''}
        </div>
    `;
};

const getDraftCategoryOptions = () => {
    const seen = new Set();
    return ads_config.drafts.reduce((options, draft) => {
        if (!draft.category_id || seen.has(String(draft.category_id))) {
            return options;
        }
        seen.add(String(draft.category_id));
        options.push({
            label: getCategoryName(draft.category_id) || `#${draft.category_id}`,
            value: String(draft.category_id),
        });
        return options;
    }, []);
};

const getDraftLocationOptions = () => {
    const seen = new Set();
    return ads_config.drafts.reduce((options, draft) => {
        const locationData = draft.location_data || {};
        const candidates = [
            locationData.region_id && locationData.region_name ? {
                value: `region:${locationData.region_id}`,
                label: locationData.region_name,
            } : null,
            locationData.city_id && locationData.city_name ? {
                value: `city:${locationData.city_id}`,
                label: [locationData.city_name, locationData.region_name].filter(Boolean).join(', '),
            } : null,
            locationData.district_id && locationData.district_name ? {
                value: `district:${locationData.district_id}`,
                label: [locationData.city_name, locationData.district_name].filter(Boolean).join(', '),
            } : null,
            locationData.zone_id && locationData.zone_name ? {
                value: `zone:${locationData.zone_id}`,
                label: [locationData.city_name, locationData.zone_name].filter(Boolean).join(', '),
            } : null,
        ].filter(Boolean);

        candidates.forEach((candidate) => {
            if (!seen.has(candidate.value)) {
                seen.add(candidate.value);
                options.push(candidate);
            }
        });
        return options;
    }, []);
};

const getDraftSortOptions = () => ([
    {label: t('ads:workspace.draftSortTitleAsc', 'Title: A-Z'), value: 'title'},
    {label: t('ads:workspace.draftSortTitleDesc', 'Title: Z-A'), value: '-title'},
    {label: t('ads:workspace.draftSortPriceAsc', 'Price: Lowest'), value: 'price'},
    {label: t('ads:workspace.draftSortPriceDesc', 'Price: Highest'), value: '-price'},
    {label: t('ads:workspace.draftSortIdAsc', 'ID: Oldest first'), value: 'id'},
    {label: t('ads:workspace.draftSortIdDesc', 'ID: Newest first'), value: '-id'},
]);

const getAccountOptions = () => (ads_config.accounts || [])
    .map((account) => {
        const accountId = account.id ?? account.account_id;
        return {
            label: `#${accountId} · ${account.name || account.email || 'Account'}`,
            value: String(accountId || ''),
        };
    })
    .filter((account) => account.value);

const renderWorkspaceCommonModals = () => {
    const root = $('#ads-workspace-common-modals');
    if (!root.length) {
        return;
    }
    if (root.find('#workspace-csv-import-modal').length) {
        return;
    }

    root.html(`
        <div class="modal fade" id="workspace-csv-import-modal" tabindex="-1" aria-labelledby="workspace-csv-import-title" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title" id="workspace-csv-import-title">${t('ads:workspace.importCsv', 'Import drafts')}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('ads:workspace.close', 'Close')}"></button>
                    </div>
                    <div class="modal-body">
                        <form id="workspace-csv-import-form" class="workspace-form">
                            <input type="file" id="workspace-csv-file" class="form-control" accept=".csv,.zip,text/csv,application/zip" required>
                            <div class="workspace-helper-text">${t('ads:workspace.csvHint', 'Upload a CSV file or backup ZIP with drafts.csv and images/.')}</div>
                            <div class="workspace-csv-import-error d-none" role="alert"></div>
                        </form>
                    </div>
                    <div class="modal-footer workspace-modal-footer">
                        <button type="submit" form="workspace-csv-import-form" class="filled-button">${t('ads:workspace.importCsvButton', 'Import')}</button>
                    </div>
                </div>
            </div>
        </div>
    `);
};

const draftCardHtml = (draft) => {
    const images = Array.isArray(draft.images) ? draft.images : [];
    const firstImage = images.length > 0 && images[0] && images[0].url ? images[0].url : '';
    const imageSection = firstImage
        ? `<img class="inventory-img" src="${escapeHtml(firstImage)}" alt="${escapeHtml(draft.title || '')}">`
        : '<div class="inventory-img draft-inventory-img-placeholder"></div>';
    const categoryTree = createDraftCategoryTree(draft.category_id) || `<li>${escapeHtml(getCategoryName(draft.category_id) || `#${draft.category_id}`)}</li>`;
    const locationLabel = getDraftLocationLabel(draft);
    const price = formatDraftPrice(draft);
    const publicationMeta = draftPublicationMetaHtml(draft);
    const publishDraftLabel = t('ads:workspace.publishDraftButton', 'Publish');
    const draftEditUrl = escapeHtml(localizeAppPageUrl(urls.draftEditPage(draft.id)));

    return `
        <div class="inventory-row draft-inventory-row" data-draft-id="${escapeHtml(draft.id)}">
            <div class="inventory-checkbox draft-inventory-checkbox">
                <input class="form-check-input ogo-checkbox big-switch workspace-draft-checkbox" type="checkbox" value="${escapeHtml(draft.id)}" ${isDraftSelected(draft.id) ? 'checked' : ''}>
            </div>
            <div class="inventory ml-2">
                <div class="inventory-card-main">
                    <div class="inventory-top d-flex">
                        <div class="inventory-card-media">${imageSection}</div>
                        <div class="inventory-data">
                            <div class="inventory-title">
                                <a href="${draftEditUrl}" class="inventory-title-text" target="_blank">${escapeHtml(draft.title || '')}</a>
                                ${price ? `<div class="inventory-price">${escapeHtml(price)}</div>` : '<div class="inventory-price"></div>'}
                            </div>
                            <div class="inventory-detail d-flex justify-content-between">
                                <div class="inventory-detail-left">
                                    <div class="inventory-card-taxonomy">
                                        <ul class="inventory-category">${categoryTree}</ul>
                                        ${locationLabel ? `<div class="inventory-location">${escapeHtml(locationLabel)}</div>` : ''}
                                    </div>
                                    ${publicationMeta}
                                </div>
                                <div class="inventory-detail-right">
                                    ${draftBadgesHtml(draft)}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="inventory-bottom d-flex justify-content-between">
                        <div class="inventory-card-identity">
                            <span class="inventory-id">ID: ${escapeHtml(draft.id)}</span>
                            ${draft.contact_name ? `<div class="account-name">${escapeHtml(draft.contact_name)}</div>` : ''}
                        </div>
                        <div class="button-section d-flex justify-content-end align-items-center flex-wrap inventory-card-actions">
                            <button type="button" class="inventory-btn inventory-btn--secondary workspace-download-draft-backup" data-draft-id="${escapeHtml(draft.id)}">${t('ads:backup.cardButton', 'Backup')}</button>
                            <button type="button" class="inventory-btn inventory-btn--secondary workspace-delete-draft" data-draft-id="${escapeHtml(draft.id)}">${t('ads:workspace.deleteDraft', 'Delete')}</button>
                            <a href="${draftEditUrl}" class="inventory-btn inventory-btn--secondary">${t('ads:workspace.editDraft', 'Edit')}</a>
                            <button type="button" class="inventory-btn inventory-btn--primary workspace-publish-draft" data-draft-id="${escapeHtml(draft.id)}">${publishDraftLabel}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
};

const getFilteredPlans = () => {
    const status = (ads_config.planFilters && ads_config.planFilters.status) || 'all';
    if (status === 'all') {
        return ads_config.plans;
    }
    return ads_config.plans.filter((plan) => plan.status === status);
};

const getPlanStatusOptions = () => {
    const statuses = [...new Set((ads_config.plans || []).map((plan) => plan.status).filter(Boolean))];
    return [
        {label: t('ads:workspace.allPlanStatuses', 'All statuses'), value: 'all'},
        ...statuses.map((status) => ({label: status, value: status})),
    ];
};

const getPlanTypeLabel = (planType) => (
    ({
        price_update: t('ads:workspace.planTypePriceUpdate', 'Price update'),
        status_update: t('ads:workspace.planTypeStatusUpdate', 'Status update'),
        publication: t('ads:workspace.planTypePublication', 'Publication'),
    })[planType] || t('ads:workspace.planTypePublication', 'Publication')
);

const planRowsHtml = () => getFilteredPlans().map((plan) => {
    const planId = escapeHtml(plan.id);
    const planStatus = String(plan.status || '');
    return `
        <tr>
            <td>${planId}</td>
            <td>${escapeHtml(plan.name)}</td>
            <td><span class="badge text-bg-light">${escapeHtml(getPlanTypeLabel(plan.plan_type))}</span></td>
            <td><span class="badge text-bg-light">${escapeHtml(plan.slot_source)}</span></td>
            <td><span class="badge text-bg-${planStatus.includes('error') ? 'danger' : 'secondary'}">${escapeHtml(planStatus)}</span></td>
            <td>${escapeHtml(plan.items_count)}</td>
            <td class="workspace-actions-cell">
                <button class="btn btn-sm btn-outline-dark workspace-load-results" data-plan-id="${planId}">${t('ads:workspace.viewResults', 'Results')}</button>
                ${['price_update', 'status_update'].includes(plan.plan_type) ? '' : `<button class="btn btn-sm btn-outline-success workspace-schedule-plan" data-plan-id="${planId}">${t('ads:workspace.queuePlan', 'Queue')}</button>`}
            </td>
        </tr>
    `;
}).join('');

const resultsHtml = () => {
    const selectedResults = ads_config.selectedPlanResults;
    if (!selectedResults) {
        return `<div class="workspace-empty">${t('ads:workspace.resultsEmpty', 'Pick a plan to inspect execution results.')}</div>`;
    }

    return selectedResults.items.map((item) => `
        <div class="workspace-result-card ${safeClassToken(item.status)}">
            <div class="workspace-result-top">
                <div>
                    <strong>#${escapeHtml(item.id)}</strong> · ${escapeHtml(item.source_type)}
                </div>
                <span class="badge text-bg-${item.status === 'success' ? 'success' : item.status === 'failed' ? 'danger' : 'secondary'}">${escapeHtml(item.status)}</span>
            </div>
            <div class="workspace-result-meta">${t('ads:workspace.account', 'Account')}: ${escapeHtml(item.target_account_id || '-')} · ${t('ads:workspace.scheduled', 'Scheduled')}: ${escapeHtml(item.scheduled_at || '-')}${item.payload_snapshot && item.payload_snapshot.operation_type ? ` · ${escapeHtml(item.payload_snapshot.operation_type)}` : ''}</div>
            <div class="workspace-result-error">${escapeHtml(item.latest_attempt && item.latest_attempt.error_message ? item.latest_attempt.error_message : '')}</div>
            <div class="workspace-actions-inline">
                <button class="btn btn-sm btn-outline-dark workspace-load-attempts" data-item-id="${escapeHtml(item.id)}">${t('ads:workspace.viewAttempts', 'Attempts')}</button>
            </div>
        </div>
    `).join('');
};

const attemptsHtml = () => {
    const selectedHistory = ads_config.selectedPlanItemHistory;
    if (!selectedHistory) {
        return `<div class="workspace-empty">${t('ads:workspace.attemptsEmpty', 'Attempt history will appear here.')}</div>`;
    }

    return selectedHistory.attempts.map((attempt) => `
        <div class="workspace-attempt-card">
            <div><strong>#${escapeHtml(attempt.attempt_number)}</strong> · <span class="badge text-bg-light">${escapeHtml(attempt.status)}</span></div>
            <div class="workspace-result-meta">${escapeHtml(attempt.started_at || '')}${attempt.finished_at ? ` → ${escapeHtml(attempt.finished_at)}` : ''}</div>
            <div class="workspace-result-error">${escapeHtml(attempt.error_message || '')}</div>
        </div>
    `).join('');
};

const getFilteredDrafts = () => {
    const filters = ads_config.draftFilters || {};
    const selectedCategories = arrayFromSelectValue(filters.categories);
    const selectedLocations = arrayFromSelectValue(filters.locations);
    const archiveFilter = filters.archive || 'active';
    const search = String(filters.search || '').trim().toLowerCase();

    let drafts = [...ads_config.drafts];
    drafts = drafts.filter((draft) => {
        const categoryMatch = selectedCategories.length === 0 || selectedCategories.includes(String(draft.category_id));
        const draftLocationValues = getDraftLocationFilterValues(draft);
        const locationMatch = selectedLocations.length === 0 || selectedLocations.some((value) => draftLocationValues.includes(value));
        const archiveMatch = archiveFilter === 'all'
            || (archiveFilter === 'archived' && draft.status === 'archived')
            || (archiveFilter === 'active' && draft.status !== 'archived');

        if (!categoryMatch || !locationMatch || !archiveMatch) {
            return false;
        }

        if (!search) {
            return true;
        }

        const searchable = [
            draft.id,
            draft.title,
            draft.rendered_description,
            draft.plain_description,
            draft.status,
            getCategoryName(draft.category_id),
            getDraftLocationLabel(draft),
            draft.contact_name,
            ...(Array.isArray(draft.contact_phones) ? draft.contact_phones : []),
        ].join(' ').toLowerCase();

        return searchable.includes(search);
    });

    switch (filters.sort) {
        case 'title':
            drafts.sort((a, b) => String(a.title || '').localeCompare(String(b.title || ''), i18next.language));
            break;
        case '-title':
            drafts.sort((a, b) => String(b.title || '').localeCompare(String(a.title || ''), i18next.language));
            break;
        case 'price':
            drafts.sort((a, b) => getDraftNumericPrice(a) - getDraftNumericPrice(b));
            break;
        case '-price':
            drafts.sort((a, b) => getDraftNumericPrice(b) - getDraftNumericPrice(a));
            break;
        case 'id':
            drafts.sort((a, b) => String(a.id || '').localeCompare(String(b.id || '')));
            break;
        case '-id':
            drafts.sort((a, b) => String(b.id || '').localeCompare(String(a.id || '')));
            break;
        default:
            break;
    }

    return drafts;
};

const updateDraftSelectionUi = () => {
    const selectedCount = ads_config.selectedDraftIds.length;
    const totalCount = Number(ads_config.draftFilters.totalCount || 0);
    const visibleCheckboxes = $('.workspace-draft-checkbox');
    const visibleCheckedCount = visibleCheckboxes.filter(':checked').length;
    const allVisibleSelected = visibleCheckboxes.length > 0 && visibleCheckedCount === visibleCheckboxes.length;
    const selectAll = $('#workspace-draft-select-all, .workspace-draft-mobile-select-all');

    selectAll.prop('checked', allVisibleSelected);
    selectAll.each(function () {
        this.indeterminate = visibleCheckedCount > 0 && visibleCheckedCount < visibleCheckboxes.length;
    });

    $('.workspace-draft-selection-text').text(
        t('ads:workspace.selectedDraftsBulkCount', 'Selected drafts: {{count}} of {{total}}', {
            count: selectedCount,
            total: totalCount,
        })
    );
    $('.workspace-download-selected-drafts').prop('disabled', selectedCount === 0);
    $('.workspace-publish-selected-drafts').prop('disabled', selectedCount === 0);
    $('.workspace-delete-selected-drafts').prop('disabled', selectedCount === 0);
    $('.ads-mobile-bulk-bar--drafts').toggleClass('is-visible', selectedCount > 0);
};

const draftLoadingHtml = () => `
    ${loadingStateHtml({
        title: t('ads:dom.spinnerLoadingText', 'Loading'),
        className: 'workspace-drafts-loading',
    })}
`;

const draftErrorHtml = (message) => `
    <div class="workspace-load-error" role="alert">
        <span>${escapeHtml(message || t('ads:workspace.loadError', 'Could not load data.'))}</span>
    </div>
`;

const renderDraftPage = (drafts) => {
    $('#workspace-drafts-list').html(drafts.length
        ? drafts.map(draftCardHtml).join('')
        : emptyStateHtml({
            title: t('ads:workspace.noDraftsMatched', 'No drafts match the current filters.'),
            className: 'workspace-empty workspace-empty-card',
        }));
    updateDraftSelectionUi();
    makeDraftSticky();
};

const updateDraftsList = () => {
    if (ads_config.draftsLoading) {
        ads_config.draftFilters = ads_config.draftFilters || {};
        ads_config.draftFilters.totalCount = 0;
        $('#workspace-drafts-count, #workspace-drafts-bottom-count').text('');
        $('#draft-pagination').empty();
        $('#workspace-drafts-list').html(draftLoadingHtml());
        updateDraftSelectionUi();
        makeDraftSticky();
        return;
    }

    if (ads_config.draftsError) {
        ads_config.draftFilters = ads_config.draftFilters || {};
        ads_config.draftFilters.totalCount = 0;
        $('#workspace-drafts-count, #workspace-drafts-bottom-count').text('');
        $('#draft-pagination').empty();
        $('#workspace-drafts-list').html(draftErrorHtml(ads_config.draftsError));
        updateDraftSelectionUi();
        makeDraftSticky();
        return;
    }

    const filteredDrafts = getFilteredDrafts();
    ads_config.draftFilters = ads_config.draftFilters || {};
    ads_config.draftFilters.totalCount = filteredDrafts.length;
    $('#workspace-drafts-count').text(t('ads:workspace.draftsCount', '{{count}} drafts', {count: filteredDrafts.length}));
    $('#workspace-drafts-bottom-count').text(t('ads:workspace.draftsCount', '{{count}} drafts', {count: filteredDrafts.length}));

    if (!filteredDrafts.length) {
        $('#draft-pagination').empty();
        renderDraftPage([]);
        return;
    }

    $('#draft-pagination').pagination({
        dataSource: filteredDrafts,
        pageSize: Number(ads_config.draftFilters.limit || 50),
        pageRange: 3,
        hideOnlyOnePage: true,
        className: 'pagination justify-content-center',
        pageClassName: 'page-item',
        prevClassName: 'page-item',
        nextClassName: 'page-item',
        activeClassName: 'active',
        disableClassName: 'disabled',
        ulClassName: 'pagination',
        prevText: '&laquo;',
        nextText: '&raquo;',
        callback: renderDraftPage,
    });
};

const initDraftVirtualSelect = (selector, options, config = {}) => {
    const element = document.querySelector(selector);
    if (!element || typeof VirtualSelect === 'undefined') {
        return;
    }

    VirtualSelect.init({
        ele: selector,
        options,
        zIndex: 1000,
        position: 'bottom',
        maxWidth: '700px',
        silentInitialValueSet: true,
        ...config,
    });
};

const initDraftFilters = () => {
    const filters = ads_config.draftFilters || {};
    initDraftVirtualSelect('#draft-category-filter', getDraftCategoryOptions(), {
        multiple: true,
        search: true,
        selectedValue: filters.categories,
        placeholder: t('ads:workspace.filterCategory', 'Category'),
        noOptionsText: t('ads:workspace.noDraftFilterOptions', 'No options'),
        noSearchResultsText: t('ads:workspace.noDraftFilterResults', 'Nothing found'),
        searchPlaceholderText: t('ads:workspace.filterSearchPlaceholder', 'Search'),
    });
    initDraftVirtualSelect('#draft-location-filter', getDraftLocationOptions(), {
        multiple: true,
        search: true,
        selectedValue: filters.locations,
        placeholder: t('ads:workspace.filterLocation', 'Location'),
        noOptionsText: t('ads:workspace.noDraftFilterOptions', 'No options'),
        noSearchResultsText: t('ads:workspace.noDraftFilterResults', 'Nothing found'),
        searchPlaceholderText: t('ads:workspace.filterSearchPlaceholder', 'Search'),
    });
    initDraftVirtualSelect('#draft-sort', getDraftSortOptions(), {
        search: false,
        selectedValue: filters.sort,
        placeholder: t('ads:workspace.filterSort', 'Sort'),
    });
};

const getDraftFilterSnapshot = () => ({
    categories: arrayFromSelectValue(ads_config.draftFilters.categories),
    locations: arrayFromSelectValue(ads_config.draftFilters.locations),
    readiness: String(ads_config.draftFilters.readiness || 'all'),
    archive: String(ads_config.draftFilters.archive || 'active'),
    sort: String(ads_config.draftFilters.sort || ''),
    search: String(ads_config.draftFilters.search || ''),
});

const updateDraftFilterControls = () => {
    const filters = ads_config.draftFilters || {};
    setDraftVirtualSelectValue('#draft-category-filter', arrayFromSelectValue(filters.categories));
    setDraftVirtualSelectValue('#draft-location-filter', arrayFromSelectValue(filters.locations));
    setDraftVirtualSelectValue('#draft-sort', filters.sort || '');
    $('#draft-search').val(filters.search || '');
    $('.draft-search-clear')
        .toggleClass('visible', Boolean(filters.search))
        .toggleClass('invisible', !filters.search);
};

const getDraftActiveFilterCount = () => {
    const filters = getDraftFilterSnapshot();
    let count = 0;
    if (filters.categories.length) count += 1;
    if (filters.locations.length) count += 1;
    if (filters.sort) count += 1;
    if (filters.search.trim()) count += 1;
    return count;
};

const updateDraftFilterSummary = () => {
    const count = getDraftActiveFilterCount();
    $('[data-ads-filter-active-count="drafts"]')
        .text(t('ads:scheduledActions.activeFiltersCount', '{{count}} active', {count}))
        .toggleClass('d-none', count <= 0);

    const search = String(ads_config.draftFilters.search || '').trim();
    $('[data-ads-filter-search-chip="drafts"]')
        .text(search ? t('ads:dom.searchChip', `Search: ${search}`, {query: search}) : '')
        .toggleClass('d-none', !search);
};

const restoreDraftFilterSnapshot = (snapshot) => {
    if (!snapshot) {
        return;
    }
    ads_config.draftFilters = {
        ...ads_config.draftFilters,
        categories: [...(snapshot.categories || [])],
        locations: [...(snapshot.locations || [])],
        readiness: snapshot.readiness || 'all',
        archive: snapshot.archive || 'active',
        sort: snapshot.sort || '',
        search: snapshot.search || '',
    };
    updateDraftFilterControls();
    updateDraftFilterSummary();
};

const resetDraftFilters = () => restoreDraftFilterSnapshot({
    categories: [],
    locations: [],
    readiness: 'all',
    archive: 'active',
    sort: '',
    search: '',
});

const syncDraftFilterPanelPlacement = () => {
    const card = document.querySelector('[data-ads-filter-card="drafts"]');
    if (!card) {
        return;
    }
    const isMobile = window.matchMedia(ADS_FILTER_MOBILE_QUERY).matches;
    const target = document.querySelector(isMobile
        ? '[data-ads-filter-drawer-slot="drafts"]'
        : '[data-ads-filter-inline-slot="drafts"]');
    if (target && card.parentElement !== target) {
        target.appendChild(card);
    }
    updateDraftFilterSummary();
};

const renderDraftsPane = () => {
    const root = $('#ads-workspace-drafts');
    if (!root.length) {
        return;
    }

    root.html(`
        <div class="workspace-shell">
            <div class="ads-page-mobile-filter-row" data-ads-filter-context="drafts">
                <button type="button"
                        class="outline-button ads-page-filter-open"
                        data-bs-toggle="modal"
                        data-bs-target="#ads-draft-filter-drawer"
                        data-ads-filter-drawer-open="drafts">
                    ${t('ads:scheduledActions.filtersButton', 'Filters')}
                </button>
                <span class="ads-page-mobile-filter-chip ads-page-mobile-active-filter-count d-none" data-ads-filter-active-count="drafts"></span>
                <span class="ads-page-mobile-filter-chip ads-page-mobile-search-chip d-none" data-ads-filter-search-chip="drafts"></span>
            </div>

            <div class="ads-page-filter-inline-slot" data-ads-filter-inline-slot="drafts">
            <div class="workspace-draft-filter-card ads-page-filter-card ads-page-filter-card--drafts" data-ads-filter-card="drafts">
            <div class="workspace-draft-filters ads-page-filters ads-page-filters--drafts">
                <div class="workspace-draft-search">
                    <input class="form-control og-input" id="draft-search" type="text" value="${escapeHtml(ads_config.draftFilters.search || '')}" placeholder="${t('ads:workspace.searchDrafts', 'Search drafts')}" maxlength="120">
                    <button type="button" class="input-clear-button draft-search-clear ${ads_config.draftFilters.search ? 'visible' : 'invisible'}" aria-label="${t('ads:workspace.clearSearch', 'Clear search')}"><i class="input-clear-icon"></i></button>
                </div>
                <div class="workspace-draft-filter" id="draft-category-filter"></div>
                <div class="workspace-draft-filter" id="draft-location-filter"></div>
                <div class="workspace-draft-filter workspace-draft-filter--sort" id="draft-sort"></div>
            </div>
            </div>
            </div>

            <div class="modal fade ads-page-filter-drawer" id="ads-draft-filter-drawer" tabindex="-1" aria-labelledby="ads-draft-filter-drawer-title" aria-hidden="true" data-ads-filter-drawer="drafts">
                <div class="modal-dialog ads-page-filter-drawer-dialog modal-dialog-end">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="ads-draft-filter-drawer-title">${t('ads:scheduledActions.filtersButton', 'Filters')}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('ads:workspace.close', 'Close')}"></button>
                        </div>
                        <div class="modal-body">
                            <div class="ads-page-filter-drawer-slot" data-ads-filter-drawer-slot="drafts"></div>
                        </div>
                        <div class="modal-footer ads-page-filter-drawer-footer">
                            <button type="button" class="outline-button ads-page-filter-reset" data-ads-filter-context="drafts">${t('ads:scheduledActions.resetFilters', 'Reset filters')}</button>
                            <button type="button" class="filled-button ads-page-filter-apply" data-ads-filter-context="drafts">${t('ads:scheduledActions.applyFilters', 'Apply')}</button>
                        </div>
                    </div>
                </div>
            </div>

            <div class="workspace-drafts-meta">
                <span id="workspace-drafts-count"></span>
            </div>
            <div id="workspace-draft-bulk-test">
                <div id="workspace-draft-bulk-container">
                    <div class="workspace-draft-selection-bar-shell">
                        <div class="workspace-draft-selection-bar">
                            <label class="bulk-label">
                                <input id="workspace-draft-select-all" class="form-check-input ogo-checkbox big-switch" type="checkbox" value="all">
                            </label>
                            <span class="bulk-text workspace-draft-selection-text">${t('ads:workspace.selectedDraftsBulkCount', 'Selected drafts: 0 of 0', {count: 0, total: 0})}</span>
                            <div class="workspace-draft-bulk-actions">
                                <button type="button" class="bulk-button workspace-download-selected-drafts" disabled>
                                    <span class="bulk-button-label">${t('ads:backup.bulkButton', 'Backup')}</span>
                                </button>
                                <button type="button" class="bulk-button workspace-delete-selected-drafts" disabled>
                                    <span class="bulk-button-label">${t('ads:workspace.deleteDraftsButton', 'Delete')}</span>
                                </button>
                                <button type="button" class="bulk-button workspace-publish-selected-drafts" disabled>
                                    <span class="bulk-button-label">${t('ads:workspace.publishDraftsButton', 'Publish')}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="ads-mobile-bulk-bar ads-mobile-bulk-bar--drafts" role="region" aria-label="${t('ads:workspace.selectedDraftsBulkCount', 'Selected drafts: 0 of 0', {count: 0, total: 0})}">
                <div class="ads-mobile-bulk-main">
                    <label class="ads-mobile-bulk-check">
                        <input class="form-check-input workspace-draft-mobile-select-all" type="checkbox" value="all">
                    </label>
                    <span class="ads-mobile-bulk-count workspace-draft-selection-text">${t('ads:workspace.selectedDraftsBulkCount', 'Selected drafts: 0 of 0', {count: 0, total: 0})}</span>
                </div>
                <div class="ads-mobile-bulk-actions">
                    <button type="button" class="bulk-button ads-mobile-bulk-action workspace-download-selected-drafts" disabled>
                        <span class="bulk-button-label">${t('ads:backup.bulkButton', 'Backup')}</span>
                    </button>
                    <button type="button" class="bulk-button ads-mobile-bulk-action workspace-publish-selected-drafts" disabled>
                        <span class="bulk-button-label">${t('ads:workspace.publishDraftsButton', 'Publish')}</span>
                    </button>
                    <button type="button" class="bulk-button ads-mobile-bulk-action workspace-delete-selected-drafts" disabled>
                        <span class="bulk-button-label">${t('ads:workspace.deleteDraftsButton', 'Delete')}</span>
                    </button>
                </div>
            </div>
            <div id="workspace-draft-import-report"></div>
            <div id="workspace-drafts-list" class="workspace-drafts-list"></div>
            <div class="workspace-drafts-bottom">
                <span id="workspace-drafts-bottom-count"></span>
                <nav id="draft-pagination"></nav>
                <ul id="draft-limit-select" class="d-flex align-items-center limit-select">
                    <li class="d-flex align-items-center justify-content-center page-limit draft-page-limit ${ads_config.draftFilters.limit === 50 ? 'page-limit-selected' : ''}">50</li>
                    <li class="d-flex align-items-center justify-content-center page-limit draft-page-limit ${ads_config.draftFilters.limit === 100 ? 'page-limit-selected' : ''}">100</li>
                    <li class="d-flex align-items-center justify-content-center page-limit draft-page-limit ${ads_config.draftFilters.limit === 200 ? 'page-limit-selected' : ''}">200</li>
                </ul>
            </div>

            <div class="modal fade" id="workspace-delete-drafts-modal" tabindex="-1" aria-labelledby="workspace-delete-drafts-title" aria-hidden="true">
                <div class="modal-dialog modal-dialog-centered">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title" id="workspace-delete-drafts-title">${t('ads:workspace.deleteDraftsButton', 'Delete')}</h5>
                            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${t('ads:workspace.close', 'Close')}"></button>
                        </div>
                        <div class="modal-body">
                            <div id="workspace-delete-drafts-description"></div>
                        </div>
                        <div class="modal-footer">
                            <button id="workspace-delete-drafts-confirm" type="button" class="filled-button btn-red">${t('ads:workspace.deleteDraftsButton', 'Delete')}</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `);
    initDraftFilters();
    syncDraftFilterPanelPlacement();
    updateDraftsList();
    showDraftImportReport(ads_config.draftImportReport);
};

const renderPlannerPane = () => {
    const root = $('#ads-workspace-planner');
    if (!root.length) {
        return;
    }

    const planRows = planRowsHtml();
    root.html(`
        <div class="workspace-shell">
            <div class="workspace-header">
                <div>
                    <h2 class="workspace-title">${t('ads:workspace.planner', 'Planner')}</h2>
                </div>
            </div>
            <div class="workspace-card">
                <div class="workspace-card-title">${t('ads:workspace.createPlan', 'Generate scheduled plan')}</div>
                <form id="workspace-plan-form" class="workspace-form">
                    <input type="text" id="planner-name" class="form-control" placeholder="${t('ads:workspace.planName', 'Plan name')}" required>
                    <div class="workspace-form-row">
                        <select id="planner-source-type" class="form-select">
                            <option value="local_draft">${t('ads:workspace.localDraftSource', 'Local drafts')}</option>
                            <option value="existing_olx_ad">${t('ads:workspace.existingAdsSource', 'Existing OLX ads')}</option>
                        </select>
                        <select id="planner-slot-source" class="form-select">
                            <option value="paid_packet">${t('ads:workspace.paidPacket', 'Paid packets')}</option>
                            <option value="free_limit">${t('ads:workspace.freeLimit', 'Free limits')}</option>
                        </select>
                        <input type="datetime-local" id="planner-start-at" class="form-control" required>
                        <input type="number" id="planner-interval" class="form-control" min="0" value="30" placeholder="${t('ads:workspace.interval', 'Interval minutes')}" required>
                    </div>
                    <div class="workspace-form-row workspace-source-group" id="planner-local-draft-group">
                        <select id="planner-draft-ids" class="form-select" multiple size="6"></select>
                        <select id="planner-account-ids" class="form-select" multiple size="6"></select>
                    </div>
                    <div class="workspace-form-row workspace-source-group d-none" id="planner-existing-ads-group">
                        <select id="planner-existing-ads" class="form-select" multiple size="8"></select>
                    </div>
                    <div class="workspace-helper-text">${t('ads:workspace.slotSourceNote', 'Paid packets are checked by category. Free limits cannot be verified in advance and will show warning-only behavior.')}</div>
                    <div class="workspace-actions-inline">
                        <button type="button" id="planner-preview-button" class="btn btn-outline-dark">${t('ads:workspace.previewPlan', 'Preview')}</button>
                        <button type="submit" class="btn btn-dark">${t('ads:workspace.createPlanButton', 'Create and queue')}</button>
                    </div>
                </form>
            </div>
            <div class="workspace-card mt-3">
                <div class="workspace-card-title">${t('ads:workspace.preview', 'Preview')}</div>
                <div id="workspace-plan-preview">${t('ads:workspace.previewEmpty', 'Preview will appear here.')}</div>
            </div>
            <div class="workspace-grid workspace-grid-results mt-3">
                <div class="workspace-card">
                    <div class="workspace-card-title">${t('ads:workspace.plansList', 'Plans')}</div>
                    <div class="workspace-plan-filter-card">
                        <div class="workspace-draft-filter" id="planner-status-filter"></div>
                    </div>
                    <div class="workspace-table-wrap">
                        <table class="table workspace-table">
                            <thead><tr><th>ID</th><th>${t('ads:workspace.planName', 'Plan')}</th><th>${t('ads:workspace.planType', 'Type')}</th><th>${t('ads:workspace.slotSource', 'Slot source')}</th><th>${t('ads:workspace.status', 'Status')}</th><th>${t('ads:workspace.items', 'Items')}</th><th></th></tr></thead>
                            <tbody>${planRows || `<tr><td colspan="7" class="workspace-empty-cell">${t('ads:workspace.noPlans', 'No plans yet.')}</td></tr>`}</tbody>
                        </table>
                    </div>
                </div>
                <div class="workspace-card">
                    <div class="workspace-card-title">${t('ads:workspace.executionResults', 'Execution results')}</div>
                    <div id="workspace-results-container">${resultsHtml()}</div>
                </div>
                <div class="workspace-card">
                    <div class="workspace-card-title">${t('ads:workspace.attemptHistory', 'Attempt history')}</div>
                    <div id="workspace-attempts-container">${attemptsHtml()}</div>
                </div>
            </div>
        </div>
    `);
    initDraftVirtualSelect('#planner-status-filter', getPlanStatusOptions(), {
        search: false,
        selectedValue: (ads_config.planFilters && ads_config.planFilters.status) || 'all',
        placeholder: t('ads:workspace.filterPlanStatus', 'Plan status'),
    });
};

const renderWorkspace = () => {
    renderWorkspaceCommonModals();
    renderDraftsPane();
    showWorkspaceTab(ads_config.activeWorkspaceTab);
};

const hydratePlannerOptions = () => {
    const draftOptions = ads_config.drafts
        .filter((draft) => draft.is_publishable)
        .map((draft) => `<option value="${escapeHtml(draft.id)}">#${escapeHtml(draft.id)} · ${escapeHtml(draft.title || '')}</option>`)
        .join('');
    $('#planner-draft-ids').html(draftOptions);

    const accountOptions = getAccountOptions()
        .map((account) => `<option value="${escapeHtml(account.value)}">${escapeHtml(account.label)}</option>`)
        .join('');
    $('#planner-account-ids').html(accountOptions);

    const existingAdsOptions = (ads_config.all_ads || []).map((ad) => {
        const label = `#${ad.id} · ${ad.title} · ${ad.account_name || ad.account_id} · ${ad.status}`;
        return `<option value="${escapeHtml(ad.id)}" data-account-id="${escapeHtml(ad.account_id)}" data-category-id="${escapeHtml(ad.category_id)}" data-zone-id="${escapeHtml(ad.location && ad.location.zone_id ? ad.location.zone_id : '')}">${escapeHtml(label)}</option>`;
    }).join('');
    $('#planner-existing-ads').html(existingAdsOptions);
};

const renderPlanPreview = (preview) => {
    if (preview.placement_plan) {
        $('#workspace-plan-preview').html(renderPlacementPlan(preview.placement_plan, {
            activeFilter: 'all',
            allowExclude: false,
        }));
        return;
    }
    const html = preview.items && preview.items.length ? preview.items.map((item) => `
        <div class="workspace-preview-item">
            <div><strong>${escapeHtml(item.source_type)}</strong> · ${t('ads:workspace.account', 'Account')} ${escapeHtml(item.target_account_id)}</div>
            <div class="workspace-result-meta">${escapeHtml(item.scheduled_at)}</div>
            ${item.slot_warning ? `<div class="workspace-warning">${escapeHtml(item.slot_warning)}</div>` : ''}
        </div>
    `).join('') : `<div class="workspace-empty">${t('ads:workspace.previewEmpty', 'Preview will appear here.')}</div>`;
    $('#workspace-plan-preview').html(html);
};

const updateWorkspaceTabIndicator = () => {
    syncSegmentedTabs(document);
};

const updateWorkspacePageHeading = (tab) => {
    const headingText = tab === 'drafts'
        ? t('ads:workspace.drafts', 'Drafts')
        : t('ads:workspace.adsPageTitle', 'Ads');
    $('#big-page-name').text(headingText);
};

const updateSecondNavWorkspaceState = (tab) => {
    const nextTab = normalizeWorkspaceTab(tab);
    const label = nextTab === 'drafts'
        ? t('ads:workspace.drafts', 'Drafts')
        : t('ads:workspace.adsPageTitle', 'Ads');

    $('[data-second-nav-workspace-label]').text(label);
    $('[data-second-nav-workspace-trigger]').addClass('active');
    $('[data-second-nav-workspace-item]')
        .removeClass('active')
        .filter(`[data-second-nav-workspace-item="${nextTab}"]`)
        .addClass('active');
};

const showWorkspaceTab = (tab, options = {}) => {
    const nextTab = normalizeWorkspaceTab(tab);
    ads_config.activeWorkspaceTab = nextTab;
    updateWorkspacePageHeading(nextTab);
    updateSecondNavWorkspaceState(nextTab);

    $('.ads-workspace-tab')
        .removeClass('active')
        .attr('aria-selected', 'false');
    $(`.ads-workspace-tab[data-workspace-tab="${nextTab}"]`)
        .addClass('active')
        .attr('aria-selected', 'true');

    $('.ads-workspace-pane').removeClass('active');
    $(`.ads-workspace-pane[data-workspace-pane="${nextTab}"]`).addClass('active');
    updateWorkspaceTabIndicator();
    makeSticky();
    makeDraftSticky();
    if (options.updateUrl) {
        updateWorkspaceTabUrl(nextTab);
    }
};

const collectGeneratedPayload = () => {
    const sourceType = $('#planner-source-type').val();
    const slotSource = $('#planner-slot-source').val();
    const payload = {
        name: $('#planner-name').val().trim(),
        source_type: sourceType,
        slot_source: slotSource,
        schedule: {
            start_at: new Date($('#planner-start-at').val()).toISOString(),
            interval_minutes: Number($('#planner-interval').val() || 0),
        },
    };

    if (sourceType === 'local_draft') {
        payload.local_draft_ids = ($('#planner-draft-ids').val() || []).map(normalizeDraftId).filter(Boolean);
        payload.target_account_ids = ($('#planner-account-ids').val() || []).map((value) => Number(value));
    } else {
        payload.existing_ads = ($('#planner-existing-ads option:selected').toArray()).map((option) => ({
            source_olx_ad_id: option.value,
            target_account_id: Number(option.dataset.accountId),
            category_id: Number(option.dataset.categoryId),
            zone_id: option.dataset.zoneId || null,
        }));
    }

    return payload;
};

const handlePlanPreview = async (event) => {
    const button = event && event.currentTarget ? event.currentTarget : '#planner-preview-button';
    try {
        await withButtonPending(button, async () => {
            const response = await previewGeneratedPlan(collectGeneratedPayload());
            renderPlanPreview(response.preview);
        });
    } catch (error) {
        displayError(JSON.stringify(error.errors || error));
    }
};

const handlePlanCreate = async (event) => {
    event.preventDefault();
    const submitButtons = getFormSubmitButtons(event.currentTarget);
    const payload = collectGeneratedPayload();
    const idempotencyKey = generatedPlanAttempt.getKey(payload);
    let response;
    try {
        response = await withButtonPending(
            submitButtons,
            () => createGeneratedPlan(payload, idempotencyKey),
        );
    } catch (error) {
        generatedPlanAttempt.handleError(error);
        displayError(
            getPostingOperationErrorMessage(error, i18next.t.bind(i18next))
            || JSON.stringify(error.errors || error),
        );
        return;
    }
    generatedPlanAttempt.complete();
    try {
        ads_config.selectedPlanResults = null;
        ads_config.selectedPlanItemHistory = null;
        if (response && response.plan && response.plan.id) {
            const resultsResponse = await fetchPlanResults(response.plan.id);
            ads_config.selectedPlanResults = resultsResponse.results;
        }
        ads_config.activeWorkspaceTab = 'planner';
        await refreshWorkspaceData();
        showWorkspaceTab('planner');
        showToast({
            message: t('ads:workspace.planCreated', 'Plan created and queued'),
            variant: 'success',
            action: {
                label: t('ads:placementAccounts.openScheduledActions', 'Open scheduled actions'),
                href: localizeAppPageUrl(urls.scheduledActionsPage),
            },
        });
    } catch (renderError) {
        console.error('Accepted generated plan feedback failed.', renderError);
    }
};

const hideCsvImportModal = () => {
    const element = document.querySelector('#workspace-csv-import-modal.show');
    if (!element) {
        return Promise.resolve();
    }
    return new Promise((resolve) => {
        element.addEventListener('hidden.bs.modal', resolve, {once: true});
        if (window.bootstrap?.Modal) {
            const modal = window.bootstrap.Modal.getOrCreateInstance(element);
            if (modal._isTransitioning) {
                element.addEventListener('shown.bs.modal', () => modal.hide(), {once: true});
            } else {
                modal.hide();
            }
            return;
        }
        $(element).modal('hide');
    });
};

const handleCsvImport = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const fileInput = form.querySelector('#workspace-csv-file');
    const file = fileInput && fileInput.files ? fileInput.files[0] : null;
    const errorContainer = form.querySelector('.workspace-csv-import-error');
    if (errorContainer) {
        errorContainer.textContent = '';
        errorContainer.classList.add('d-none');
    }
    if (!file) {
        if (errorContainer) {
            errorContainer.textContent = t('ads:workspace.csvFileRequired', 'CSV file is required');
            errorContainer.classList.remove('d-none');
        }
        return;
    }

    const submitButtons = getFormSubmitButtons(form);
    try {
        await withButtonPending(submitButtons, async () => {
            const attempt = getDraftImportAttempt(file);
            const response = await importDraftCsv(file, attempt.idempotencyKey);
            if (!response?.import || typeof response.import !== 'object') {
                throw {
                    status: 502,
                    errors: {request: [t('ads:workspace.csvImportRequestFailed', 'Draft import failed. Try again.')]},
                };
            }
            const result = normalizeDraftImportResult(response.import);
            ads_config.draftImportReport = result.severity === 'success' ? null : result;
            ads_config.activeWorkspaceTab = 'drafts';
            await hideCsvImportModal();
            form.reset();
            ads_config.draftImportAttempt = null;
            await refreshWorkspaceData();
            showWorkspaceTab('drafts', {updateUrl: true});
            if (result.severity === 'success') {
                clearDraftImportReport();
                showToast({
                    message: t(
                        'ads:workspace.csvImportSuccess',
                        'Imported {{count}} drafts.',
                        {count: result.imported},
                    ),
                    variant: 'success',
                });
            } else {
                showDraftImportReport(result);
                focusDraftImportReport();
            }
        });
    } catch (error) {
        const message = getDraftImportErrorMessage(error);
        if (Number(error?.status || 0) >= 400 && Number(error?.status || 0) < 500 && errorContainer) {
            errorContainer.textContent = message;
            errorContainer.classList.remove('d-none');
        } else {
            showToast({message, variant: 'danger'});
        }
    }
};

const getDraftsByIds = (draftIds) => {
    const selectedIds = new Set((draftIds || []).map((draftId) => String(draftId)));
    return (ads_config.drafts || []).filter((draft) => selectedIds.has(String(draft.id)));
};

const showBackupExportResult = (manifest) => {
    const failedImages = Number((manifest && manifest.failed_images && manifest.failed_images.length) || 0);
    if (failedImages > 0) {
        showToast({
            message: t('ads:backup.partialImagesWarning', 'Backup downloaded, but {{count}} images could not be embedded. Their URLs were kept in CSV.', {
                count: failedImages,
            }),
            variant: 'warning',
        });
        return;
    }
    showToast({message: t('ads:backup.success', 'Backup downloaded.'), variant: 'success'});
};

const handleDownloadDraftBackups = async (draftIds, triggerButton = null) => {
    const drafts = getDraftsByIds(draftIds);
    if (!drafts.length) {
        displayError(t('ads:backup.selectRequired', 'Select at least one item.'));
        return;
    }
    const buttons = triggerButton
        ? $(triggerButton)
        : $('.workspace-download-draft-backup, .workspace-download-selected-drafts');
    try {
        await withButtonPending(buttons, async () => {
            const manifest = await downloadDraftBackupZip(drafts);
            showBackupExportResult(manifest);
        });
    } catch (error) {
        showToast({message: t('ads:backup.error', 'Unable to create backup.'), variant: 'danger'});
    } finally {
        updateDraftSelectionUi();
    }
};

const openDraftPublishPlacementPage = (draftIds = getSelectedDraftIds(), executionMode = 'now') => {
    const normalizedDraftIds = (draftIds || []).map(normalizeDraftId).filter(Boolean);
    if (!normalizedDraftIds.length) {
        displayError(t('ads:workspace.selectDraftsRequired', 'Select at least one draft.'));
        return;
    }
    const accounts = getAccountOptions();
    if (!accounts.length) {
        displayError(t('ads:workspace.selectAccountsRequired', 'Select at least one OLX account.'));
        return;
    }
    try {
        openDraftPublicationPlacement({
            draftIds: normalizedDraftIds,
            accounts,
            executionMode,
            returnUrl: window.location.href,
        });
    } catch (error) {
        if (!isPlacementOperationStorageError(error)) {
            throw error;
        }
        draftPublicationOpenFailureToast?.dismiss();
        draftPublicationOpenFailureToast = showToast({
            message: t(
                'ads:placementOperation.openFailed',
                'The operation could not be opened. Please try again.',
            ),
            variant: 'danger',
        });
    }
};

const getDeleteDraftsConfirmMessage = (draftIds) => (
    draftIds.length === 1
        ? t('ads:workspace.confirmDeleteDraft', 'Delete this draft permanently?')
        : t('ads:workspace.confirmDeleteDrafts', 'Delete {{count}} drafts permanently?', {count: draftIds.length})
);

const openDeleteDraftsModal = (draftIds, triggerButton = null) => {
    const normalizedDraftIds = (draftIds || []).map(normalizeDraftId).filter(Boolean);
    if (!normalizedDraftIds.length) {
        displayError(t('ads:workspace.selectDraftsRequired', 'Select at least one draft.'));
        return;
    }

    pendingDeleteDraftIds = normalizedDraftIds;
    pendingDeleteDraftTrigger = triggerButton;
    $('#workspace-delete-drafts-description').text(getDeleteDraftsConfirmMessage(normalizedDraftIds));
    $('#workspace-delete-drafts-modal').modal('show');
};

const handleDeleteDrafts = async (draftIds, triggerButton = null, confirmButton = null) => {
    const normalizedDraftIds = (draftIds || []).map(normalizeDraftId).filter(Boolean);
    if (!normalizedDraftIds.length) {
        displayError(t('ads:workspace.selectDraftsRequired', 'Select at least one draft.'));
        return;
    }
    const $buttons = triggerButton ? $(triggerButton) : $('.workspace-delete-draft, .workspace-delete-selected-drafts');
    const pendingButtons = confirmButton ? $(confirmButton) : $buttons;

    try {
        await withButtonPending(pendingButtons, async () => {
            $buttons.prop('disabled', true);
            const response = await deleteDrafts({draft_ids: normalizedDraftIds});
            const deletedIds = new Set((response.items || [])
                .filter((item) => item.status === 'deleted')
                .map((item) => String(item.draft_id)));
            ads_config.selectedDraftIds = ads_config.selectedDraftIds.filter((draftId) => !deletedIds.has(String(draftId)));
            $('#workspace-delete-drafts-modal').modal('hide');
            await refreshWorkspaceData();
            showWorkspaceTab('drafts');

            const deletedCount = Number(response.deleted_count || 0);
            const errorCount = Number(response.error_count || 0);
            if (deletedCount && errorCount) {
                showToast({
                    message: t('ads:workspace.deleteDraftsPartialSuccess', 'Deleted {{deleted}} drafts. Errors: {{errors}}.', {
                        deleted: deletedCount,
                        errors: errorCount,
                    }),
                    variant: 'warning',
                });
            } else if (deletedCount) {
                showToast({
                    message: t('ads:workspace.deleteDraftsSuccess', 'Deleted {{count}} drafts.', {count: deletedCount}),
                    variant: 'success',
                });
            }
        });
    } catch (error) {
        displayError(JSON.stringify(error.errors || error));
    } finally {
        $buttons.prop('disabled', false);
        updateDraftSelectionUi();
    }
};

const handleLoadResults = async (planId) => {
    try {
        const response = await fetchPlanResults(planId);
        ads_config.selectedPlanResults = response.results;
        ads_config.selectedPlanItemHistory = null;
        ads_config.activeWorkspaceTab = 'planner';
        renderWorkspace();
        showWorkspaceTab('planner');
    } catch (error) {
        displayError(JSON.stringify(error.errors || error));
    }
};

const handleLoadAttempts = async (itemId) => {
    try {
        const response = await fetchPlanItemAttempts(itemId);
        ads_config.selectedPlanItemHistory = response.history;
        ads_config.activeWorkspaceTab = 'planner';
        renderWorkspace();
        showWorkspaceTab('planner');
    } catch (error) {
        displayError(JSON.stringify(error.errors || error));
    }
};

const togglePlannerSourceGroups = () => {
    const sourceType = $('#planner-source-type').val();
    $('#planner-local-draft-group').toggleClass('d-none', sourceType !== 'local_draft');
    $('#planner-existing-ads-group').toggleClass('d-none', sourceType !== 'existing_olx_ad');
};

export const bindWorkspaceEvents = () => {
    bindSegmentedTabsAutoSync();
    $(window)
        .off('resize.adsDraftFilters')
        .on('resize.adsDraftFilters', syncDraftFilterPanelPlacement);

    $(window)
        .off('popstate.adsWorkspaceTabs')
        .on('popstate.adsWorkspaceTabs', () => {
            showWorkspaceTab(getWorkspaceTabFromLocation());
            syncDraftFilterPanelPlacement();
        });

    $('#content').on('click', '.ads-workspace-tab', function () {
        showWorkspaceTab($(this).data('workspace-tab'), {updateUrl: true});
        syncDraftFilterPanelPlacement();
    });

    $('#content').on('click', '[data-ads-filter-drawer-open="drafts"]', function () {
        syncDraftFilterPanelPlacement();
    });

    $('#content').on('show.bs.modal', '#ads-draft-filter-drawer', function () {
        syncDraftFilterPanelPlacement();
        draftFilterDrawerSnapshot = getDraftFilterSnapshot();
        draftFilterDrawerCommitted = false;
    });
    $('#content').on('hidden.bs.modal', '#ads-draft-filter-drawer', function () {
        if (!draftFilterDrawerCommitted) {
            restoreDraftFilterSnapshot(draftFilterDrawerSnapshot);
            updateDraftsList();
        }
        draftFilterDrawerSnapshot = null;
        draftFilterDrawerCommitted = false;
    });
    $('#content').on('click', '.ads-page-filter-apply[data-ads-filter-context="drafts"]', function () {
        draftFilterDrawerCommitted = true;
        updateDraftFilterSummary();
        updateDraftsList();
        $('#ads-draft-filter-drawer').modal('hide');
    });
    $('#content').on('click', '.ads-page-filter-reset[data-ads-filter-context="drafts"]', function () {
        draftFilterDrawerCommitted = true;
        resetDraftFilters();
        updateDraftsList();
        $('#ads-draft-filter-drawer').modal('hide');
    });

    $('#content').on('change', '#draft-category-filter', function () {
        ads_config.draftFilters.categories = arrayFromSelectValue($(this).val());
        updateDraftFilterSummary();
        updateDraftsList();
    });
    $('#content').on('change', '#draft-location-filter', function () {
        ads_config.draftFilters.locations = arrayFromSelectValue($(this).val());
        updateDraftFilterSummary();
        updateDraftsList();
    });
    $('#content').on('change', '#draft-sort', function () {
        ads_config.draftFilters.sort = String($(this).val() || '');
        updateDraftFilterSummary();
        updateDraftsList();
    });
    $('#content').on('input', '#draft-search', function () {
        ads_config.draftFilters.search = $(this).val();
        $('.draft-search-clear').toggleClass('visible', Boolean(ads_config.draftFilters.search)).toggleClass('invisible', !ads_config.draftFilters.search);
        updateDraftFilterSummary();
        updateDraftsList();
    });
    $('#content').on('click', '.draft-search-clear', function () {
        ads_config.draftFilters.search = '';
        $('#draft-search').val('').focus();
        $(this).removeClass('visible').addClass('invisible');
        updateDraftFilterSummary();
        updateDraftsList();
    });
    $('#content').on('click', '#draft-limit-select li', function () {
        if ($(this).hasClass('page-limit-selected')) {
            return;
        }
        $('#draft-limit-select li').removeClass('page-limit-selected');
        $(this).addClass('page-limit-selected');
        ads_config.draftFilters.limit = Number($(this).text().trim()) || 50;
        updateDraftsList();
    });
    $('#content').on('change', '#workspace-draft-select-all, .workspace-draft-mobile-select-all', function () {
        const checked = $(this).is(':checked');
        const currentIds = new Set(ads_config.selectedDraftIds.map(String));
        $('.workspace-draft-checkbox').each(function () {
            const id = normalizeDraftId($(this).val());
            $(this).prop('checked', checked);
            if (checked) {
                currentIds.add(id);
            } else {
                currentIds.delete(id);
            }
        });
        ads_config.selectedDraftIds = Array.from(currentIds);
        updateDraftSelectionUi();
    });
    $('#content').on('change', '.workspace-draft-checkbox', function () {
        const id = normalizeDraftId($(this).val());
        const selectedIds = new Set(ads_config.selectedDraftIds.map(String));
        if ($(this).is(':checked')) {
            selectedIds.add(id);
        } else {
            selectedIds.delete(id);
        }
        ads_config.selectedDraftIds = Array.from(selectedIds);
        updateDraftSelectionUi();
    });

    $('#content').on('submit', '#workspace-csv-import-form', handleCsvImport);
    $('#content').on('change', '#workspace-csv-file', function () {
        const file = this.files?.[0] || null;
        ads_config.draftImportAttempt = file ? {
            file,
            idempotencyKey: createDraftImportIdempotencyKey(),
        } : null;
        const errorContainer = this.form?.querySelector('.workspace-csv-import-error');
        if (errorContainer) {
            errorContainer.textContent = '';
            errorContainer.classList.add('d-none');
        }
    });
    $('#content').on('click', '.draft-import-report__close', function () {
        ads_config.draftImportReport = null;
        clearDraftImportReport();
    });
    $('#content').on('click', '.workspace-download-selected-drafts', function (event) {
        event.preventDefault();
        handleDownloadDraftBackups(getSelectedDraftIds(), this);
    });
    $('#content').on('click', '.workspace-delete-selected-drafts', function () {
        openDeleteDraftsModal(getSelectedDraftIds(), this);
    });
    $('#content').on('click', '.workspace-publish-selected-drafts', function (event) {
        event.preventDefault();
        openDraftPublishPlacementPage(getSelectedDraftIds(), 'now');
    });
    $('#content').on('click', '.workspace-publish-draft', function (event) {
        event.preventDefault();
        openDraftPublishPlacementPage([$(this).data('draft-id')], 'now');
    });
    $('#content').on('click', '.workspace-download-draft-backup', function (event) {
        event.preventDefault();
        handleDownloadDraftBackups([$(this).data('draft-id')], this);
    });
    $('#content').on('click', '.workspace-delete-draft', function () {
        openDeleteDraftsModal([$(this).data('draft-id')], this);
    });
    $('#content').on('click', '#workspace-delete-drafts-confirm', function () {
        handleDeleteDrafts(pendingDeleteDraftIds, pendingDeleteDraftTrigger, this);
    });
    $('#content').on('hidden.bs.modal', '#workspace-delete-drafts-modal', function () {
        pendingDeleteDraftIds = [];
        pendingDeleteDraftTrigger = null;
    });
    $('#content').on('click', '#planner-preview-button', handlePlanPreview);
    $('#content').on('submit', '#workspace-plan-form', handlePlanCreate);
    $('#content').on('change', '#planner-source-type', togglePlannerSourceGroups);
    $('#content').on('change', '#planner-status-filter', function () {
        ads_config.planFilters.status = String($(this).val() || 'all');
        renderPlannerPane();
        hydratePlannerOptions();
        togglePlannerSourceGroups();
    });
    $('#content').on('click', '.workspace-load-results', function () {
        handleLoadResults($(this).data('plan-id'));
    });
    $('#content').on('click', '.workspace-load-attempts', function () {
        handleLoadAttempts($(this).data('item-id'));
    });
    $('#content').on('click', '.workspace-schedule-plan', async function () {
        const button = this;
        try {
            await withButtonPending(button, async () => {
                await schedulePlan($(button).data('plan-id'));
                ads_config.activeWorkspaceTab = 'planner';
                await refreshWorkspaceData();
                showWorkspaceTab('planner');
                showToast({
                    message: t('ads:workspace.planQueued', 'Plan queued'),
                    variant: 'success',
                    action: {
                        label: t('ads:placementAccounts.openScheduledActions', 'Open scheduled actions'),
                        href: localizeAppPageUrl(urls.scheduledActionsPage),
                    },
                });
            });
        } catch (error) {
            displayError(JSON.stringify(error.errors || error));
        }
    });
};

export const refreshWorkspaceData = async () => {
    ads_config.draftsLoading = true;
    ads_config.draftsError = '';
    try {
        const draftsResponse = await fetchDrafts();

        ads_config.drafts = draftsResponse.drafts || [];
    } catch (error) {
        ads_config.draftsError = extractWorkspaceErrorMessage(error);
        displayError(ads_config.draftsError);
    } finally {
        ads_config.draftsLoading = false;
        renderWorkspace();
    }
};

export const initWorkspace = async () => {
    renderWorkspace();
    bindWorkspaceEvents();
    await refreshWorkspaceData();
};
